import { useState, useEffect } from "react";

/**
 * Dialog Authentication Hook
 * Handles session exchange when the password manager dialog opens.
 * Extracts sessionId from URL, exchanges it for an access token and OID.
 *
 * Usage in PasswordManagerDialog:
 * const { isAuthenticated, oid, accessToken, error, loading } = useDialogAuth();
 *
 * Session storage keys used:
 * - "dialog_oid"                  — cached OID from exchange response
 * - "dialog_accessToken"          — cached access token
 * - "dialog_sessionId"            — which session was exchanged
 * - "dialog_expiresAt"            — token expiry
 * - "dialog_exchange_in_progress" — atomic lock to prevent double-exchange
 */
export const useDialogAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [oid, setOid] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const authenticateDialog = async () => {
      try {
        // Step 1: Extract sessionId from URL
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get("sid");

        if (!sessionId) {
          if (isMounted) {
            setError("Keine Sitzungs-ID gefunden");
            setLoading(false);
          }
          return;
        }

        // Step 2: Check if already authenticated (session cache)
        const cachedOid = sessionStorage.getItem("dialog_oid");
        const cachedToken = sessionStorage.getItem("dialog_accessToken");
        const cachedSessionId = sessionStorage.getItem("dialog_sessionId");

        // If already authenticated with same sessionId, reuse cached data
        if (cachedOid && cachedToken && cachedSessionId === sessionId) {
          console.log("Using cached authentication:", cachedOid);
          if (isMounted) {
            setOid(cachedOid);
            setAccessToken(cachedToken);
            setIsAuthenticated(true);
            setLoading(false);
          }
          return;
        }

        // Step 3: Atomic check-and-set for exchange in progress
        const exchangeInProgress = sessionStorage.getItem(
          "dialog_exchange_in_progress"
        );

        if (exchangeInProgress === sessionId) {
          console.log(
            "Exchange already in progress for this session, waiting..."
          );
          // Poll sessionStorage until exchange completes
          let pollCount = 0;
          const pollInterval = setInterval(() => {
            pollCount++;
            const newCachedOid = sessionStorage.getItem("dialog_oid");
            const newCachedToken = sessionStorage.getItem("dialog_accessToken");
            const newCachedSessionId =
              sessionStorage.getItem("dialog_sessionId");

            console.log(
              `Polling ${pollCount}: oid=${!!newCachedOid}, token=${!!newCachedToken}, sessionId=${newCachedSessionId}`
            );

            if (
              newCachedOid &&
              newCachedToken &&
              newCachedSessionId === sessionId
            ) {
              console.log("Exchange completed, updating state from cache");
              clearInterval(pollInterval);
              if (isMounted) {
                setOid(newCachedOid);
                setAccessToken(newCachedToken);
                setIsAuthenticated(true);
                setLoading(false);
              }
            } else if (pollCount > 100) {
              // 5 seconds (50ms * 100)
              console.error("Exchange timeout after 5 seconds");
              clearInterval(pollInterval);
              if (isMounted) {
                setError("Authentifizierung timeout");
                setLoading(false);
              }
            }
          }, 50);
          return;
        }

        // Step 4: Mark exchange as in progress (atomic operation)
        if (exchangeInProgress) {
          console.warn(
            "Another session exchange is already in progress:",
            exchangeInProgress
          );
          if (isMounted) {
            setError("Eine andere Sitzung wird bereits authentifiziert");
            setLoading(false);
          }
          return;
        }

        sessionStorage.setItem("dialog_exchange_in_progress", sessionId);
        console.log("Starting exchange for session:", sessionId);

        // Step 5: Exchange sessionId for access token + OID
        const { exchangeDialogSession } = await import(
          "../api/dialogSessionApi"
        );
        const exchangeResult = await exchangeDialogSession(sessionId);

        if (!exchangeResult.success) {
          sessionStorage.removeItem("dialog_exchange_in_progress");
          if (isMounted) {
            setError(exchangeResult.message);
            setLoading(false);
          }
          return;
        }

        const authData = exchangeResult.data;
        console.log("Session exchanged successfully, oid:", authData?.oid);

        // Step 6: Store authentication data in sessionStorage FIRST
        // (before isMounted check so parallel renders can also read it)
        sessionStorage.setItem("dialog_oid", authData.oid);
        sessionStorage.setItem("dialog_accessToken", authData.accessToken);
        sessionStorage.setItem("dialog_expiresAt", authData.expiresAt);
        sessionStorage.setItem("dialog_sessionId", sessionId);
        sessionStorage.removeItem("dialog_exchange_in_progress");

        console.log("Stored auth data in sessionStorage:", {
          oid: authData.oid,
          sessionId,
        });

        // Step 7: Update React state only if still mounted
        if (!isMounted) {
          console.log("Component unmounted, skipping state update");
          return;
        }

        setOid(authData.oid);
        setAccessToken(authData.accessToken);
        setIsAuthenticated(true);
        setLoading(false);
      } catch (err) {
        console.error("Dialog authentication failed:", err);
        sessionStorage.removeItem("dialog_exchange_in_progress");
        if (isMounted) {
          setError("Authentifizierung fehlgeschlagen");
          setLoading(false);
        }
      }
    };

    authenticateDialog();

    return () => {
      isMounted = false;
    };
  }, []); // Run once on mount

  return {
    isAuthenticated,
    oid,
    accessToken,
    error,
    loading,
  };
};
